
import { Separator } from '@/components/ui/separator';
import { formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { isAllowancePayeTaxable } from '@/lib/payrollAllowances';

interface Allowance {
  id: string;
  name: string;
  amount: number;
  value?: string;
  type?: string;
}

interface SalaryBreakdownProps {
  basicPay: number | undefined;
  basicSalary?: number;
  allowances: Allowance[];
  grossSalary: number;
}

export const SalaryBreakdown = ({ basicPay, basicSalary, allowances, grossSalary }: SalaryBreakdownProps) => {
  const basicAmount = basicPay || basicSalary || 0;
  const hasAllowances = allowances && allowances.length > 0;
  
  return (
    <div className="p-6 border b border-gray-400 rounded-md">
      <h3 className="font-semibold text-lg mb-3">Earnings (Income)</h3>
      <div className="space-y-2">
        <div className="flex justify-between py-1 px-2 rounded-sm">
          <span>Basic Pay</span>
          <span className="font-medium">{formatCurrency(basicAmount)}</span>
        </div>
        
        {/* Allowances */}
        {hasAllowances ? (
          allowances.map(allowance => (
            <div key={allowance.id} className="flex justify-between items-start gap-2 py-1 px-3 rounded-sm">
              <span className="pr-4 flex flex-wrap items-center gap-1">
                {allowance.name}
                <Badge
                  variant="outline"
                  className={
                    isAllowancePayeTaxable(allowance)
                      ? 'bg-amber-50 text-amber-900 border-amber-200 text-[10px] font-normal'
                      : 'bg-slate-50 text-slate-600 border-slate-200 text-[10px] font-normal'
                  }
                >
                  {isAllowancePayeTaxable(allowance) ? 'PAYE' : 'Exempt'}
                </Badge>
              </span>
              <span>{allowance.value ? allowance.value : formatCurrency(allowance.amount)}</span>
            </div>
          ))
        ) : (
          <div className="text-sm text-gray-500 italic">
            No allowances configured for this payslip
          </div>
        )}
        
        <Separator />
        <div className="flex justify-between font-semibold py-1 px-2 rounded-sm">
          <span>Gross Pay</span>
          <span>{formatCurrency(grossSalary)}</span>
        </div>
        
        {/* Show calculation breakdown if no allowances */}
        {!hasAllowances && basicAmount > 0 && (
          <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-50 rounded">
            <div className="font-medium mb-1">Calculation:</div>
            <div>Basic Pay: {formatCurrency(basicAmount)}</div>
            <div>Allowances: {formatCurrency(0)}</div>
            <div>Gross Pay: {formatCurrency(basicAmount)}</div>
          </div>
        )}
      </div>
    </div>
  );
};
